import { useEffect, useMemo, useState } from "react";
import type { PersonProfile } from "../../shared/types";
import { apiPerson } from "../lib/api";
import { cleanDisplayName, digits, formatCurrency } from "../lib/format";
import { navigate } from "../lib/useHashRoute";
import { StatusBadge } from "./StatusBadge";
import { IconAlert, IconBuilding, IconInfo, IconUser, IconWeb } from "./Icons";

/** Painel da pessoa: empresas do QSA público e quem aparece junto nelas. */
export function PersonPage({ name, doc, de }: { name: string; doc?: string; de?: string }) {
  const [profile, setProfile] = useState<PersonProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setProfile(null);
    setError(null);
    apiPerson(name, doc, controller.signal, de)
      .then(setProfile)
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Não foi possível carregar o perfil.");
      });
    return () => controller.abort();
  }, [name, doc, de]);

  // Capital das empresas em atividade: somar o de baixadas dá um número irreal.
  const totalCapital = useMemo(
    () =>
      profile?.companies
        .filter((company) => !/baixada|inapta|suspensa|nula|inativa/i.test(company.status ?? ""))
        .reduce((sum, company) => sum + (company.capital ?? 0), 0) ?? 0,
    [profile],
  );

  if (error) {
    return (
      <main className="page container">
        <div className="state-box">
          <IconAlert size={32} />
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={() => navigate({ page: "home" })}>
            Voltar para a busca
          </button>
        </div>
      </main>
    );
  }

  if (!profile) {
    return (
      <main className="page container">
        <div className="state-box loading">
          <span className="spinner" />
          <p>Reunindo as empresas desta pessoa…</p>
        </div>
      </main>
    );
  }

  const firstCompany = profile.companies.find((company) => digits(company.cnpj).length === 14);

  return (
    <main className="page container">
      {profile.demo && (
        <div className="demo-banner">
          <IconAlert size={16} />
          <span>{profile.message}</span>
        </div>
      )}

      <div className="page-head">
        <div className="page-head-main">
          <h1 className="page-title">{cleanDisplayName(profile.name)}</h1>
          <p className="page-subtitle">Pessoa física no quadro societário público</p>
          <div className="page-badges">
            {profile.maskedDocument && <span className="badge plain mono">{profile.maskedDocument}</span>}
            {profile.ageGroup && <span className="badge plain">{profile.ageGroup}</span>}
            {profile.companies.length > 0 && (
              <span className="badge plain">
                {profile.companies.length} empresa{profile.companies.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </div>
        {firstCompany && (
          <div className="page-actions">
            <button
              className="btn btn-primary"
              onClick={() => navigate({ page: "network", cnpj: digits(firstCompany.cnpj) })}
            >
              <IconWeb size={16} /> Abrir teia
            </button>
          </div>
        )}
      </div>

      {profile.companies.length === 0 ? (
        // Explicação, não falha: o ícone de alerta fazia isto parecer erro.
        <div className="state-box" style={{ marginTop: 8 }}>
          <IconInfo size={28} />
          <p style={{ maxWidth: 560 }}>{profile.message}</p>
          <button className="btn btn-secondary" onClick={() => navigate({ page: "home" })}>
            Voltar para a busca
          </button>
        </div>
      ) : (
      <><div className="summary-strip">
        <div className="summary-item">
          <span className="summary-label">Empresas ativas</span>
          <span className="summary-value">{profile.activeCount}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Empresas inativas</span>
          <span className="summary-value">{profile.inactiveCount}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Pessoas relacionadas</span>
          <span className="summary-value">{profile.relatedPeople.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Capital das ativas</span>
          <span className="summary-value">{formatCurrency(totalCapital)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Praças</span>
          <span className="summary-value">{profile.cities.length || "Não informado"}</span>
        </div>
      </div>

      <div className="card-grid">
        <section className="card span-2">
          <h2>Empresas relacionadas ({profile.companies.length})</h2>
          <div className="partner-list">
            {profile.companies.map((company) => (
              <button
                className="partner-row clickable"
                key={company.cnpj}
                onClick={() => navigate({ page: "company", cnpj: digits(company.cnpj) })}
                title="Abrir a ficha da empresa"
              >
                <span aria-hidden><IconBuilding size={17} /></span>
                <span className="partner-info">
                  <span className="partner-name">{cleanDisplayName(company.name)}</span>
                  <span className="partner-meta">
                    {company.role && <span>{company.role}</span>}
                    <span className="mono">{company.cnpj}</span>
                    {company.joinedAt && <span>Desde {company.joinedAt}</span>}
                    {(company.city || company.state) && (
                      <span>{[company.city, company.state].filter(Boolean).join(" / ")}</span>
                    )}
                  </span>
                  {company.mainActivity && <span className="partner-meta"><span>{company.mainActivity}</span></span>}
                </span>
                <StatusBadge status={company.status} />
              </button>
            ))}
          </div>
        </section>

        {profile.relatedPeople.length > 0 && (
          <section className="card span-2">
            <h2>Pessoas relacionadas ({profile.relatedPeople.length})</h2>
            <p className="card-hint">
              Quem divide o quadro societário com esta pessoa em pelo menos uma empresa.
            </p>
            <div className="partner-list">
              {profile.relatedPeople.slice(0, 24).map((person) => (
                <button
                  className="partner-row clickable"
                  key={`${person.name}-${person.maskedDocument ?? ""}`}
                  onClick={() => navigate({ page: "person", name: person.name, doc: person.maskedDocument })}
                  title={`Abrir o perfil de ${person.name}`}
                >
                  <span aria-hidden><IconUser size={17} /></span>
                  <span className="partner-info">
                    <span className="partner-name">{cleanDisplayName(person.name)}</span>
                    <span className="partner-meta">
                      {person.role && <span>{person.role}</span>}
                      {person.maskedDocument && <span className="mono">{person.maskedDocument}</span>}
                      <span>
                        {person.sharedCompanies.length} empresa{person.sharedCompanies.length === 1 ? "" : "s"} em comum
                      </span>
                    </span>
                    <span className="partner-meta">
                      <span>{person.sharedCompanies.map((company) => cleanDisplayName(company.name)).join(" · ")}</span>
                    </span>
                  </span>
                  <span className="partner-go" aria-hidden>Perfil →</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {profile.cities.length > 0 && (
          <section className="card">
            <h2>Onde essas empresas estão</h2>
            <div className="chip-list">
              {profile.cities.map((city) => (
                <span className="badge plain" key={city}>{city}</span>
              ))}
            </div>
          </section>
        )}
      </div>

      </>
      )}

    </main>
  );
}
